import { useState, useEffect, useRef } from 'react';

interface UseDropdownProps {
  isOpen: boolean;
  onToggle: () => void;
  portalContainer?: Element;
  scrollThreshold?: number;
}

export function useDropdown({ isOpen, onToggle, portalContainer = document.body }: UseDropdownProps) {
  const [dropdownPosition, setDropdownPosition] = useState<{ 
    top: number; 
    left: number; 
    width: number; 
  } | null>(null);
  
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Calculate position when opening
  const calculatePosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  // Click outside handler
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        onToggle();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle]);

  // Close dropdown on scroll (only if scroll is outside the dropdown)
  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = (event: Event) => {
      // If scroll is within dropdown content, allow it
      if (dropdownRef.current && event.target && dropdownRef.current.contains(event.target as Node)) {
        return; // Don't close dropdown
      }
      
      // Otherwise, close dropdown (external scroll)
      onToggle();
    };

    const handleWheel = (event: WheelEvent) => {
      // If wheel event is within dropdown content, allow it
      if (dropdownRef.current && event.target && dropdownRef.current.contains(event.target as Node)) {
        return; // Don't close dropdown
      }
      
      // Otherwise, close dropdown (external wheel)
      onToggle();
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('scroll', handleScroll, true);
    document.addEventListener('wheel', handleWheel, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('wheel', handleWheel);
    };
  }, [isOpen, onToggle]);

  const toggleDropdown = () => {
    if (!isOpen) {
      calculatePosition();
    }
    onToggle();
  };

  return {
    triggerRef,
    dropdownRef,
    dropdownPosition,
    toggleDropdown,
    portalContainer,
  };
}